import { defineSlotRecipe } from '@chakra-ui/react';
import { comboboxAnatomy } from '@chakra-ui/react/anatomy';

export const comboboxRecipe = defineSlotRecipe({
  className: 'ac-combobox',
  slots: comboboxAnatomy.keys(),
  base: {
    root: {
      width: '100%',

      _disabled: {
        cursor: 'disabled',
      },
    },
    control: {
      border: 'subtle',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'grey.light.2',
      borderRadius: 'xl',
      backgroundColor: 'white',
      height: '40px',

      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '15px',
      lineHeight: '40px',

      fontFamily: 'acBase',

      minWidth: '160px',
      width: '100%',

      gap: '8px',

      transitionProperty: 'border, backgroundColor, background',
      transitionDuration: 'slow',
      transitionTimingFunction: 'linear',

      boxShadow: '10px 10px 15px 0px rgba(17, 17, 17, 0.02)',
      overflow: 'hidden',

      _open: {
        border: 'active',
      },

      _disabled: {
        cursor: 'disabled',
        backgroundColor: 'grey.light.0',
      },
    },
    input: {
      display: 'flex',
      padding: '0px 12px',
      justifyContent: 'flex-start',
      alignItems: 'center',
      height: '100%',
      width: '100%',

      fontSize: '15px',
      lineHeight: '40px',

      fontFamily: 'acBase',

      outline: 'none',
      border: 'none',
      backgroundColor: 'transparent',

      _disabled: {
        cursor: 'disabled',
      },
    },
    indicatorGroup: {
      display: 'flex',
      gap: '0px',
      justifyContent: 'space-between',
      height: '100%',
      alignItems: 'center',

      _disabled: {
        cursor: 'disabled',
      },
    },

    clearTrigger: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      width: '32px',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',

      _disabled: {
        background: 'transparent',
        cursor: 'disabled',
      },

      '& > svg': {
        height: '1rem',
        width: '1rem',
      },
    },
    trigger: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',

      transitionDuration: 'fast',
      transitionProperty: 'backgroundColor',
      transitionTimingFunction: 'linear',

      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      width: '32px',
      height: '100%',

      _disabled: {
        background: 'transparent',
        cursor: 'disabled',
      },

      '& > svg': {
        height: '1.2rem',
        width: '1.2rem',

        transitionDuration: 'fast',
        transitionProperty: 'transform',
        transitionTimingFunction: 'linear',
      },

      _open: {
        '& > svg': {
          transform: 'rotateZ(-180deg)',
        },
      },
    },
    itemIndicator: {
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',

      width: '15px',
      height: '15px',
      top: '50%',
      right: '8px',
      transform: 'translateY(-50%)',

      position: 'absolute',
      _checked: {
        display: 'flex',
      },
    },
    content: {
      backgroundColor: 'white',
      zIndex: 500,
      border: 'active',
      borderRadius: 'xl',
      overflow: 'hidden',
      overflowY: 'auto',
      maxHeight: 'var(--available-height)',
      fontFamily: 'acBase',
      _open: {
        animationStyle: 'menu-open',
        animationDuration: 'moderate',
      },
      _closed: {
        animationStyle: 'menu-close',
        animationDuration: 'moderate',
      },
    },
    empty: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      fontSize: '15px',
      letterSpacing: '0.15px',
      color: 'black',
      backgroundColor: 'white',
    },
    item: {
      position: 'relative',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      fontSize: '15px',
      letterSpacing: '0.15px',
      color: 'black',
      backgroundColor: 'white',

      transitionProperty: 'border, backgroundColor, background, color',
      transitionDuration: 'fast',
      transitionTimingFunction: 'linear',

      userSelect: 'none',

      _odd: {
        backgroundColor: 'grey.light.0',
      },

      _hover: {
        backgroundColor: 'halton.green/10',
        // backgroundColor: 'grey.light.1',
      },
    },
  },
  variants: {
    sizing: {
      full: {
        root: {
          flexGrow: 1,
        },
      },
    },
    size: {
      md: {
        trigger: {
          height: '40px',
        },
      },
      lg: {
        trigger: {
          height: '48px',
        },
      },
    },
    variant: {
      outline: {
        trigger: {
          borderColor: 'grey.light.3',

          _hover: {
            backgroundColor: 'grey.blue.1',
            borderColor: 'grey.dark.2',
            // boxShadow: '0px 0px 12px 0px rgba(0, 0, 0, 0.1)',
            boxShadow: '10px 10px 15px 0px rgba(17, 17, 17, 0.02)',
          },

          _disabled: {
            borderColor: 'grey.light.2',
            color: 'grey.light.2',
            cursor: 'disabled',
          },
        },
      },
      inset: {
        trigger: {
          borderRadius: 'main',
          borderColor: 'grey.light.1',
          backgroundColor: 'grey.light.0',
          color: 'grey.dark.2',

          height: '48px',

          fontSize: '15px',
          fontWeight: 400,
          letterSpacing: '0.15px',

          _hover: {
            borderColor: 'grey.dark.2',
          },
          _open: {
            borderColor: 'grey.dark.2',
          },
        },
        indicator: {
          width: '28px',
          height: '28px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',

          borderRadius: 'smaller',
          backgroundColor: 'white',
          border: '1px solid {colors.grey.light.1}',
          boxShadow: '10px 10px 15px 0px, rgba(17, 17, 17, 0.2)',

          transitionProperty: 'border, backgroundColor, background, color',
          transitionDuration: 'fast',
          transitionTimingFunction: 'linear',

          '& > svg': {
            width: '0.875rem',
            height: '0.875rem',
          },

          _open: {
            backgroundColor: 'grey.dark.2',
            color: 'white',
          },
        },
      },
      form: {
        trigger: {
          height: '44px',
          borderRadius: 'smaller',
        },
        content: {
          borderRadius: 'smaller',
        },
      },
      deviceForm: {
        trigger: {
          minWidth: '100px',
          height: '40px',
          borderRadius: 'smaller',
        },
        content: {
          borderRadius: 'smaller',
        },
      },
      assetPicker: {
        trigger: {
          height: '30px',
          width: '100%',
          padding: '10px 15px',
          borderRadius: 'small',
          borderColor: 'grey.light.1',
          fontSize: '13px',

          _hover: {
            borderColor: 'grey.dark.2',
          },
        },
        content: {
          borderRadius: 'small',
        },
        item: {
          fontSize: '13px',
          height: '30px',
          padding: '0 15px',
        },
      },
      cramped: {
        root: {
          minWidth: '120px',
        },
        trigger: {
          minWidth: '120px',
          fontSize: '12px',
          padding: '8px',
        },
        content: {
          fontSize: '12px',
        },
        item: {
          fontSize: '12px',
          padding: '0 12px',
        },
      },
      compact: {
        root: {
          minWidth: '120px',
        },
        trigger: {
          height: '30px',
          padding: '0 0.5rem',
          borderRadius: 'small',
          minWidth: '120px',

          textOverflow: 'ellipsis',
          overflow: 'hidden',
          _placeholderShown: {
            color: 'grey.light.3',
          },
        },

        content: {
          borderRadius: 'small',
        },
        item: {
          height: '30px',
          padding: '0 0.5rem',
        },
        itemIndicator: {
          '& svg': {
            height: '15px',
          },
        },
      },
    },
    error: {
      none: {
        trigger: {
          borderColor: 'grey.light.2',
          _open: {
            border: 'active',
          },
        },
      },
      missing: {
        trigger: {
          borderColor: 'halton.red',

          _open: {
            borderColor: 'halton.red',
          },
        },
      },
    },
    itemSizing: {
      auto: {
        item: {
          height: 'auto',
          paddingTop: '8px',
          paddingBottom: '8px',
        },
        itemText: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          wordBreak: 'break-word',

          maxWidth: '100%',
        },
      },
    },
    textSizing: {
      small: {
        trigger: {
          fontSize: '12px',
        },
        content: {
          fontSize: '12px',
        },
        item: {
          fontSize: '12px',
        },
      },
    },
  },
});
